import type { Agency } from "@asap/shared/schemas/agency";
import { getAgencyDisplayName } from "~/components/agency/agency.model";
import { useAgencyApi } from "~/components/agency/composables/useAgencyApi";
import { useToast } from "~/composables/useToast";

// Agencies are the company's Pappers establishments, stored when the company is created or synced
export const useAgencySelectField = (companyId: () => string) => {
  const { t } = useI18n();
  const { addToastError } = useToast();
  const { listCompanyAgencies } = useAgencyApi();

  const agencies = ref<Agency[]>([]);
  const isLoading = ref(false);

  const agencyItems = computed(() =>
    agencies.value.map((agency) => ({
      value: agency.id,
      label: getAgencyDisplayName(agency.name, agency.address?.postal_code),
      helperText: agency.address?.formatted ?? undefined,
    }))
  );

  let latestLoadId = 0;

  const loadAgencies = async () => {
    const loadId = ++latestLoadId;
    isLoading.value = true;
    try {
      const loadedAgencies = await listCompanyAgencies(companyId());
      if (loadId === latestLoadId) agencies.value = loadedAgencies;
    } catch (error) {
      if (loadId === latestLoadId) addToastError({ title: t("companies.agencySelect.loadError") }, error);
    } finally {
      if (loadId === latestLoadId) isLoading.value = false;
    }
  };

  watch(companyId, loadAgencies, { immediate: true });

  return { agencyItems, isLoading };
};
